import React from 'react'
import { Route, Redirect, RouteComponentProps } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { isMicrosoftPayment, selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { logout } from '../../services/auth/logout-handler'
import { FrontendUrls } from '../../types/urls'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { getEffectivePlan } from '@vacationtracker/shared/functions/plan-boost'
import { PaymentProcessor } from '@vacationtracker/shared/types/billing'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { selectFeatureFlagsSlice } from '../../store/feature-flags-slice'

interface IPrivateRoute {
  component: React.ComponentType<RouteComponentProps> | React.ComponentType<any>
  roles: string[]
  platform: string[]
  path: string
  plan?: SubscriptionPlanEnum
  featureFlag?: FeatureFlagEnum
  paymentProcessors?: PaymentProcessor[]
}

export const PrivateRoute = ({ component: Component, roles, platform, path, paymentProcessors, plan, featureFlag }: IPrivateRoute): React.ReactElement => {
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const isMicrosoftBillingPayment = useSelector(isMicrosoftPayment)
  const dispatch = useAppDispatch()

  return (
    <Route
      path={path}
      render={(props: RouteComponentProps) => {
        if (!authUser.id) {
          // not logged in so redirect to login page with the return url
          return <Redirect to={{ pathname: FrontendUrls.signin, state: { from: props.location } }} />
        }

        if (authCompany && !(authCompany?.subscriptionStatus === 'trialing' || authCompany?.subscriptionStatus === 'active' || authCompany?.subscriptionStatus === 'past_due')) {
          // subscription expired
          if (authUser.role === 'Admin') {
            return <Redirect to={{ pathname: FrontendUrls.subscription }} />
          } else if (isMicrosoftBillingPayment) {
            return <Redirect to={{ pathname: FrontendUrls.subscription }} />
          } else {
            logout({
              history: props.history,
              reduxDispatch: dispatch,
              redirectRoute: `${FrontendUrls.signin}?error=subscription_expired`,
              userId: authUser.id,
            })
          }
        }

        // If Admin or LocationAdmin try to open /app/my-profile page redirect to /app/users/userId page
        if ((authUser.role === 'Admin' || authUser.role === 'LocationAdmin') && path === '/app/my-profile') {
          return <Redirect to={{ pathname: `/app/users/${authUser.id}` }} />
        }

        if (roles && !roles.includes(authUser.role)) {
          // If User tries to access admin dashboard, redirect to user dashboard with query params preserved
          if (authUser.role === 'User' && (path === FrontendUrls.dashboard || path === '/app/dashboard')) {
            return <Redirect to={{ pathname: FrontendUrls.userDashboard, search: props.location.search }} />
          }
          // role not authorised so redirect to home page
          return <Redirect to={{ pathname: '/', search: props.location.search }} />
        }

        if (platform && !platform.includes(authUser?.platform)) {
          // role not authorised so redirect to home page
          return <Redirect to={{ pathname: '/' }} />
        }

        if (paymentProcessors && paymentProcessors.length > 0 && !paymentProcessors.includes(authCompany?.billing?.paymentProcessor as PaymentProcessor)) {
          // paymentProcessor not authorised so redirect to home page
          return <Redirect to={{ pathname: '/' }} />
        }
        if (plan && getEffectivePlan(authCompany) !== plan) {
          const hasFeatureFlag = featureFlag && featureFlags.includes(featureFlag)
          if (!hasFeatureFlag) {
            return <Redirect to={{ pathname: '/' }} />
          }
        }

        // authorised so return component
        return <Component {...props} />
      }}
    />
  )
}
